import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { inviteApi } from '../services/api'
import { mediaUrl } from '../services/config'
import { Users, ArrowRight, AlertCircle, Loader2, UserPlus } from 'lucide-react'

export default function InvitePage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuthStore()
  const { addServer } = useServerStore()
  const [invite, setInvite] = useState<{ server: { id: string; name: string; iconUrl?: string | null; description?: string | null } } | null>(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!code || authLoading) return

    inviteApi.get(code)
      .then((res) => {
        setInvite(res.data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Invalid invite link')
        setLoading(false)
      })
  }, [code, authLoading])

  if (authLoading || loading) {
    return (
      <div className="h-screen w-screen bg-card flex items-center justify-center">
        <Loader2 size={32} className="text-primary animate-spin" />
      </div>
    )
  }

  const handleJoin = async () => {
    if (!code) return
    setJoining(true)
    try {
      const res = await inviteApi.join(code)
      const server = res.data
      addServer(server)
      const firstChannel = server.categories?.[0]?.channels?.[0] || server.channels?.[0]
      if (firstChannel) {
        navigate(`/servers/${server.id}/channels/${firstChannel.id}`)
      } else {
        navigate('/channels/@me')
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to join the server'
      if (msg === 'Already a member') {
        setError('You are already a member of this server')
        if (invite?.server.id) {
          navigate(`/servers/${invite.server.id}/channels/@me`)
        }
      } else {
        setError(msg)
      }
    } finally {
      setJoining(false)
    }
  }

  return (
    <div className="h-screen w-screen bg-card flex items-center justify-center">
      <div className="bg-background rounded-xl p-8 w-[420px] shadow-2xl text-center">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 size={32} className="text-primary animate-spin" />
            <p className="text-muted-foreground text-sm">Loading invite...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertCircle size={48} className="text-destructive" />
            <h2 className="text-xl font-bold text-foreground">Error</h2>
            <p className="text-muted-foreground text-sm">{error}</p>
            <button
              onClick={() => navigate('/channels/@me')}
              className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:bg-primary/90 transition"
            >
              Go home
            </button>
          </div>
        ) : (
          <>
            {invite?.server.iconUrl ? (
              <img
                src={mediaUrl(invite.server.iconUrl)}
                alt=""
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-foreground text-2xl font-bold mx-auto mb-4">
                {invite?.server.name?.slice(0, 2).toUpperCase()}
              </div>
            )}
            <h2 className="text-xl font-bold text-foreground mb-1">{invite?.server.name}</h2>
            {invite?.server.description && (
              <p className="text-muted-foreground text-sm mb-4">{invite.server.description}</p>
            )}
            <div className="flex items-center justify-center gap-1 text-green-500 text-sm mb-6">
              <Users size={14} />
              <span>You've been invited to a server</span>
            </div>
            {isAuthenticated ? (
              <button
                onClick={handleJoin}
                disabled={joining}
                className="w-full py-3 bg-primary text-primary-foreground rounded font-medium hover:bg-primary/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {joining ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    Join server
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/register?invite=${code}&redirect=/invite/${code}`)}
                  className="w-full py-3 bg-primary text-primary-foreground rounded font-medium hover:bg-primary/90 transition flex items-center justify-center gap-2"
                >
                  <UserPlus size={18} />
                  Sign up
                </button>
                <p className="text-muted-foreground text-sm">
                  Already have an account?{' '}
                  <button
                    onClick={() => navigate(`/login?redirect=/invite/${code}`)}
                    className="text-primary hover:underline"
                  >
                    Log in
                  </button>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
